export class EnrichmentAPI {
  private static readonly BASE_URL = "/api/v1";
  private static readonly ENDPOINTS = {
    requestEmail: "/email_enrichment/single",
    requestPhone: "/request_email", // mock
  } as const;

  private static readonly HEADERS = {
    contentType: "Content-Type",
    jsonContentType: "application/json",
  } as const;

  static async requestEmailMock(entityId: string | number, type: "person" | "company"): Promise<{ email: string }> {
    try {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock response based on type
      const mockEmails = {
        person: ["john.doe@company.com", "jane.smith@enterprise.com", "bob.wilson@corp.net"],
        company: ["contact@company.com", "info@enterprise.com", "support@corp.net"],
      };

      // Randomly select an email for more realistic testing
      const emails = type === "person" ? mockEmails.person : mockEmails.company;
      const email = emails[Math.floor(Math.random() * emails.length)];

      // Simulate API response structure
      return { email };
    } catch (error) {
      throw new Error(`Failed to fetch email: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  // In your EnrichmentAPI class:
  static async requestEmail(entityId: string | number, type: "person" | "company"): Promise<{ email: string }> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 20000);

    try {
      const params = new URLSearchParams({
        candidateID: entityId.toString(),
        companyName: "",
      });
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.requestEmail}?${params}`, {
        method: "GET",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        credentials: "include",
        signal: controller.signal,
      });

      // Handle 404 case specifically
      if (response.status === 404) {
        logger.error("Email not found");
        return { email: "Not available" };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      // Distinguish between abort (timeout) and other errors
      if (error instanceof DOMException && error.name === "AbortError") {
        throw new Error("Request timed out");
      }
      throw new Error(`Failed to fetch email: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  static async requestPhoneMock(entityId: string | number, type: "person" | "company"): Promise<{ phone: string }> {
    try {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock response based on type
      const mockPhones = {
        person: ["+1-555-123-4567", "+1-555-234-5678", "+1-555-345-6789"],
        company: ["+1-555-987-6543", "+1-555-876-5432", "+1-555-765-4321"],
      };

      // Randomly select a phone for more realistic testing
      const phones = type === "person" ? mockPhones.person : mockPhones.company;
      const phone = phones[Math.floor(Math.random() * phones.length)];

      // Simulate API response structure
      return { phone };
    } catch (error) {
      throw new Error(`Failed to fetch phone: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async requestPhone(entityId: string | number, type: "person" | "company"): Promise<{ phone: string }> {
    // This is a mock implementation that simulates API behavior
    try {
      // Simulate network delay
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Mock response based on type
      const mockPhones = {
        person: ["+1-555-123-4567", "+1-555-234-5678", "+1-555-345-6789"],
        company: ["+1-555-987-6543", "+1-555-876-5432", "+1-555-765-4321"],
      };

      // Randomly select a phone for more realistic testing
      const phones = type === "person" ? mockPhones.person : mockPhones.company;
      const phone = phones[Math.floor(Math.random() * phones.length)];

      // Simulate API response structure
      return { phone };
    } catch (error) {
      throw new Error(`Failed to fetch phone: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }

  static async requestPhoneReal(entityId: string | number, type: "person" | "company"): Promise<{ email: string }> {
    try {
      const response = await fetch(`${this.BASE_URL}${this.ENDPOINTS.requestPhone}`, {
        method: "POST",
        headers: {
          [this.HEADERS.contentType]: this.HEADERS.jsonContentType,
        },
        body: JSON.stringify({
          entityId,
          type,
        }),
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      throw new Error(`Failed to fetch phone: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
  }
}
